import React, { memo } from "react";
import { RCCGrid } from "react-common-components";
import List from "src/components/List";
import { useTranslation } from "src/context/Translations/context";
import InfiniteScrollWithoutFetch from "src/components/InfiniteScrollWithoutFetch";
import formatNumber from "src/utils/formatNumber";
import { v4 } from "uuid";
import formatDate from "src/utils/formatDate";
import Title from "src/components/Title";
import { addYears } from "date-fns";
import ExportListSpace from "src/components/List/exportListSpace";
import * as ListStyles from "./styles";
import { IListConsultValue } from "./interface";

const ListTable: React.FC<IListConsultValue> = ({
	data,
	showHeader = true,
	showOnlyAccumulated = false,
	showColumnLastYear = true,
	showColumnPlanned = true,
	showColumnRealized = true,
	showVariationPercent = true,
	showVariationValue = false,
	isExpense,
	title,
	subsidiary,
	offsetLeft,
	coinPlannedInitials,
	coinRealizedInitials,
	onMouseDown
}) => {
	const { translate, currentLanguage } = useTranslation();

	const colTitle = (row: any) => {
		const isChild = !row.children || row.children == null || row.children.length === 0;
		return(
			<ListStyles.DivAlignCenterWithOverflow className="break-columns-in-csv-here" fixed>
				{offsetLeft > 0 && [...Array(offsetLeft / 16)].map(() => <ExportListSpace key={v4()} />)}
				<ListStyles.OverflowSpan title={row.description} style={{ marginLeft: `${offsetLeft}px`}} bold={!isChild}> 
					{row.description} 
				</ListStyles.OverflowSpan>
			</ListStyles.DivAlignCenterWithOverflow>
		);};

	const customHeaderColTitle = () => (<ListStyles.DivColumn alignStart fixed isHeader>
		<ListStyles.OverflowSpan bold>{title || translate("Estrutura")}</ListStyles.OverflowSpan>
		<ListStyles.OverflowSpan bold purple style={{fontSize: "0.8rem"}}>
			{translate("Unidade/Filial")}: {subsidiary || translate("Total")}
		</ListStyles.OverflowSpan>
	</ListStyles.DivColumn>);

	const colValues = (row: any) => {
		const isChild = !row.children || row.children == null || row.children.length === 0;
		return(
			<ListStyles.DivValues onMouseDown={onMouseDown} >
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important;"
					className="query-budget-panel"
				>
					{!showOnlyAccumulated && (
						<InfiniteScrollWithoutFetch 
							listElements={row.values}
							mapper={(value: any) => (
								<React.Fragment
									key={v4()}
								>
									{showColumnPlanned && (
										<ListStyles.TD className="value-predicted break-columns-in-csv-here">
											<ListStyles.OverflowSpan bold={!isChild}>
												{formatNumber(value.predicted, currentLanguage, 0)}
											</ListStyles.OverflowSpan>
										</ListStyles.TD>
									)}
									{showColumnRealized && (
										<ListStyles.TD className="value-realized break-columns-in-csv-here">
											<ListStyles.OverflowSpan bold={!isChild}>
												{formatNumber(value.realized, currentLanguage, 0)}
											</ListStyles.OverflowSpan>
										</ListStyles.TD>
									)}
									{showColumnLastYear && (
										<ListStyles.TD className="value-last-year break-columns-in-csv-here">
											<ListStyles.OverflowSpan bold={!isChild}>
												{formatNumber(value.lastYear, currentLanguage, 0)}
											</ListStyles.OverflowSpan>
										</ListStyles.TD>
									)}
									{showVariationValue && (
										<ListStyles.TD className="value-variation-value break-columns-in-csv-here">
											<ListStyles.OverflowSpan bold={!isChild} 
												red={(isExpense && value.variationValue > 0) || (!isExpense && value.variationValue < 0)}
												green={(isExpense && value.variationValue < 0) || (!isExpense && value.variationValue > 0)}
											>
												{formatNumber(value.variationValue, currentLanguage, 0)}
											</ListStyles.OverflowSpan>
										</ListStyles.TD>
									)}
									{showVariationPercent && (
										<ListStyles.TD className="value-variation-percent break-columns-in-csv-here">
											<ListStyles.OverflowSpan bold={!isChild}
												red={(isExpense && value.variationPercent > 0) || (!isExpense && value.variationPercent < 0)}
												green={(isExpense && value.variationPercent < 0) || (!isExpense && value.variationPercent > 0)}
											>
												{formatNumber(value.variationPercent, currentLanguage, 2)}%
											</ListStyles.OverflowSpan>
										</ListStyles.TD>
									)}
								</React.Fragment>
							)}
							defaultNumberOfElements={24}
							startIndex={0}
						/>
					)}
					{showColumnPlanned && (
						<ListStyles.TD className="value-predicted break-columns-in-csv-here">
							<ListStyles.OverflowSpan bold={!isChild}>
								{formatNumber(row.accumulatedValues.predicted, currentLanguage, 0)}
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
					{showColumnRealized && (
						<ListStyles.TD className="value-realized break-columns-in-csv-here">
							<ListStyles.OverflowSpan bold={!isChild}>
								{formatNumber(row.accumulatedValues.realized, currentLanguage, 0)}
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
					{showColumnLastYear && (
						<ListStyles.TD className="value-last-year break-columns-in-csv-here">
							<ListStyles.OverflowSpan bold={!isChild}>
								{formatNumber(row.accumulatedValues.lastYear, currentLanguage, 0)}
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
					{showVariationValue && (
						<ListStyles.TD className="value-variation-value break-columns-in-csv-here">
							<ListStyles.OverflowSpan bold={!isChild}
								red={(isExpense && row.accumulatedValues.variationValue > 0) || (!isExpense && row.accumulatedValues.variationValue < 0)}
								green={(isExpense && row.accumulatedValues.variationValue < 0) || (!isExpense && row.accumulatedValues.variationValue > 0)}
							>
								{formatNumber(row.accumulatedValues.variationValue, currentLanguage, 0)}
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
					{showVariationPercent && (
						<ListStyles.TD className="value-variation-percent break-columns-in-csv-here">
							<ListStyles.OverflowSpan bold={!isChild}
								red={(isExpense && row.accumulatedValues.variationPercent > 0) || (!isExpense && row.accumulatedValues.variationPercent < 0)}
								green={(isExpense && row.accumulatedValues.variationPercent < 0) || (!isExpense && row.accumulatedValues.variationPercent > 0)}
							>
								{formatNumber(row.accumulatedValues.variationPercent, currentLanguage, 2)}%
							</ListStyles.OverflowSpan>
						</ListStyles.TD>
					)}
				</RCCGrid>
			</ListStyles.DivValues>
		);};

	const customHeaderColValues = (rows: any) => {
		const firstRow = rows[0];
		return(
			<ListStyles.DivValues>
				<RCCGrid 
					container
					item
					styleInline="flex-wrap: unset !important;"
					className="query-budget-panel"
				>
					{!showOnlyAccumulated && firstRow.values.map((value: any) => (
						<React.Fragment key={v4()}>
							{showColumnPlanned && (
								<ListStyles.TD>
									<ListStyles.DivColumn className="break-columns-in-csv-here">
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Planejado")}
										</Title>
										{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(value.date, currentLanguage)}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
							{showColumnRealized && (
								<ListStyles.TD>
									<ListStyles.DivColumn className="break-columns-in-csv-here">
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Realizado")}
										</Title>
										{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(value.date, currentLanguage)}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
							{showColumnLastYear && (
								<ListStyles.TD>
									<ListStyles.DivColumn className="break-columns-in-csv-here">
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Anterior")}
										</Title>
										{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(addYears(new Date(value.date), -1), currentLanguage)}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
							{showVariationValue && (
								<ListStyles.TD>
									<ListStyles.DivColumn className="break-columns-in-csv-here">
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Variação")} $
										</Title>
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(value.date, currentLanguage)}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
							{showVariationPercent && (
								<ListStyles.TD>
									<ListStyles.DivColumn className="break-columns-in-csv-here">
										<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
											{translate("Variação")} %
										</Title>
										<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
											{formatDate(value.date, currentLanguage)}
										</Title>
									</ListStyles.DivColumn>
								</ListStyles.TD>
							)}
						</React.Fragment>
					))}
					{showColumnPlanned && (
						<ListStyles.TD >
							<ListStyles.DivColumn className="break-columns-in-csv-here">
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Planejado")}
								</Title>
								{coinPlannedInitials ? <small>({coinPlannedInitials})</small> : ""}
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
					{showColumnRealized && (
						<ListStyles.TD >
							<ListStyles.DivColumn className="break-columns-in-csv-here">
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Realizado")}
								</Title>
								{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
					{showColumnLastYear && (
						<ListStyles.TD >
							<ListStyles.DivColumn className="break-columns-in-csv-here">
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Anterior")}
								</Title>
								{coinRealizedInitials ? <small>({coinRealizedInitials})</small> : ""}
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
					{showVariationValue && (
						<ListStyles.TD >
							<ListStyles.DivColumn className="break-columns-in-csv-here">
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Variação")} $
								</Title>
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
					{showVariationPercent && (
						<ListStyles.TD >
							<ListStyles.DivColumn className="break-columns-in-csv-here">
								<Title size="xs" color="defaultColor" bold marginTop={0} marginBottom={0}>
									{translate("Variação")} %
								</Title>
								<Title size="xs" color="purple" bold marginTop={0} marginBottom={0}>
									{translate("Acumulado")}
								</Title>
							</ListStyles.DivColumn>
						</ListStyles.TD>
					)}
				</RCCGrid>
			</ListStyles.DivValues>
		);};

	const listChild = (dt: any) => (
		<ListTable 
			data={dt.children}
			showHeader={false}
			showOnlyAccumulated={showOnlyAccumulated}
			showColumnLastYear={showColumnLastYear}
			showColumnPlanned={showColumnPlanned}
			showColumnRealized={showColumnRealized}
			showVariationPercent={showVariationPercent}
			showVariationValue={showVariationValue}
			isExpense={isExpense}
			offsetLeft={offsetLeft + 16}
			onMouseDown={onMouseDown}
		/>
	);
    
	return(
		<>
			<List 
				data={data}
				columns={[{
					idColumn: "title",
					nameColumn: translate("Indicador"),
					justify: "left",
					cellRenderer: colTitle,
					customHeader: customHeaderColTitle,
					styleInline: "width: 17.5rem !important; min-height: 24px; z-index: 10; background: var(--li-header-color); @media(max-width: 600px){ width: 13rem !important; } @media(max-width: 400px){ width: 11rem !important; }"
				},
				{
					idColumn: "values",
					nameColumn: translate("Valores"),
					cellRenderer: colValues,
					customHeader: customHeaderColValues,
					styleInline: "flex: 1;"
				}]}
				childrenOfLine={{
					cellRenderer: listChild,
					conditionToRender: (dt: any) => dt.children && dt.children.length > 0
				}}
				showHeader={showHeader}
				headerHiddenXs={false}
				layout="table"
				defaultNumberOfElements={200}
				manuallyToggleShowMore
				styleInline={`
						width: max-content;
                        >li.list-component-header{
                            margin: 0;
                            padding: 0;
							border-bottom: 2px solid var(--border-color);
							position: unset !important;
                        }
                        >li.list-component-child{
                            padding: 0;
							background-color: var(--li-child-striped) !important;
							position: unset !important;
                        }
                        li.list-component-child.is-children{
                            padding: 0;
							margin-bottom: 0;
                        }
						@media(max-width: 600px){
						>li{
							flex-direction: row !important;
						}
					}`}
			/>
		</>
	);};

export default memo(ListTable);
